// src/utils/menu/generateMenuPdf.js
// Draws the flower menu PDF. Geometry and colors are taken from the design files
// in design_intake/001-flower-menu-pdf/ (PDF points, origin bottom-left).
import { PDFDocument, rgb, degrees } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import apiClient from "../../api/apiClient";
import interRegularUrl from "../../assets/fonts/inter/Inter-Regular.ttf";
import interMediumUrl from "../../assets/fonts/inter/Inter-Medium.ttf";
import interBoldUrl from "../../assets/fonts/inter/Inter-Bold.ttf";
import { AROMA_BY_COLOR, DEMS_TIERS, EFFECT_BY_COLOR, MENU_COLORS, POTENCY_TIERS } from "./menuMetrics";
import { BRAND_STYLES } from "./menuModel";
import { BRAND_ROW, CARD, CONTENT_SHIFT, PAGE, SECTION, paginateMenu } from "./menuLayout";

const INK = rgb(0.141, 0.121, 0.126);
const WHITE = rgb(1, 1, 1);
const TEAL = rgb(0.046, 0.235, 0.254);
const ACCENT_GREEN = rgb(0, 0.566, 0.351);
const TITLE_MINT = rgb(0.485, 0.808, 0.661);

const COLOR_FILL = {
    yellow: rgb(0.908, 0.849, 0.384),
    green: rgb(0.546, 0.795, 0.443),
    blue: rgb(0.41, 0.765, 0.884),
    navy: rgb(0.239, 0.259, 0.568),
    purple: rgb(0.569, 0.338, 0.641),
};
// Navy and purple are dark enough to need white lettering.
const LIGHT_TEXT_COLORS = new Set(["navy", "purple"]);
const labelInk = (color) => (LIGHT_TEXT_COLORS.has(color) ? WHITE : INK);

const CONTENT_LEFT = PAGE.margin;
const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;
const TEXT_LEFT = 53.29;
const TEXT_RIGHT = PAGE.width - TEXT_LEFT;

// Design coordinates, moved down to sit below the license row.
const shifted = (y) => y - CONTENT_SHIFT;

const BRAND = {
    top: PAGE.height - PAGE.margin,
    bottom: PAGE.height - PAGE.margin - BRAND_ROW.height,
    logoMaxWidth: 180,
    nameSize: 14,
};

const HEADER = {
    barBottom: shifted(713),
    barTop: shifted(756),
    ruleHeight: 3.2,
    titleY: shifted(724.88),
    dateY: shifted(723.85),
    pageLabelY: shifted(740.51),
};

const LEGEND = {
    titleY: shifted(688.83),
    headerY: shifted(689.6),
    rowY: [671.68, 654.11, 636.53, 618.97, 601.39].map(shifted),
    rowPitch: 17.572,
    effectsX: 122.51,
    swatch: { x: 122.474, width: 59.377, height: 14.342, firstBottom: shifted(668.11) },
    effectTextX: 189.64,
    potencyX: 271.44,
    potencyBar: { width: 5.005, height: 14.341, pitch: 6.795, firstBottom: shifted(668.112) },
    potencyTextX: 297.57,
    aromaX: 396.42,
    demsX: 472.91,
    dividers: [251.61, 374.32, 453.1],
    demsNoteY: [676.39, 668.49].map(shifted),
    dividerTop: shifted(697.22),
    dividerBottom: shifted(596),
};

const CARD_PARTS = {
    tabWidth: 24.591,
    lowerRowHeight: 21.3,
    barsCellWidth: 26.428,
    // Wider than the design's 40.2 so "Chemical", which the design did not include, fits.
    aromaCellWidth: 45.5,
    cellTextSize: 9,
    namePadX: 4.403,
    nameBaseline: 28.44,
    cellBaseline: 7.5,
    bar: { firstX: 4.905, pitch: 5.902, bottom: 3.939, width: 4.348, height: 13.089 },
    stroke: 0.324,
};

const PRICE = { headingX: 52.97, labelX: 52.69, valueX: 74.06, headingDrop: 19.78, firstRowDrop: 35.05, rowPitch: 16.57 };

/** Largest size (down to `minSize`) at which `text` fits `maxWidth`; ellipsizes if it still does not fit. */
function fitText(text, font, size, maxWidth, minSize = 7) {
    let fitted = size;
    while (fitted > minSize && font.widthOfTextAtSize(text, fitted) > maxWidth) fitted -= 0.5;
    let out = text;
    while (out.length > 1 && font.widthOfTextAtSize(out, fitted) > maxWidth) {
        out = `${out.slice(0, -2).trimEnd()}…`;
    }
    return { text: out, size: fitted };
}

function drawCentered(page, text, font, size, left, width, y, color = INK) {
    const fitted = fitText(text, font, size, width - 2);
    const textWidth = font.widthOfTextAtSize(fitted.text, fitted.size);
    page.drawText(fitted.text, { x: left + (width - textWidth) / 2, y, size: fitted.size, font, color });
}

function drawRight(page, text, font, size, right, y, color) {
    page.drawText(text, { x: right - font.widthOfTextAtSize(text, size), y, size, font, color });
}

function drawPotencyBars(page, x, bottom, bar, filled, pitch) {
    for (let i = 0; i < 3; i++) {
        page.drawRectangle({
            x: x + i * pitch,
            y: bottom,
            width: bar.width,
            height: bar.height,
            borderColor: INK,
            borderWidth: 0.329,
            color: i < filled ? INK : undefined,
        });
    }
}

// License row above the header. Shows the logo, the name, or both, per the chosen
// brand style: a lone logo or name sits at the left; with both, the logo is at the
// left and the name is right-aligned. If the logo is unavailable the name is used.
function drawBrandRow(page, fonts, storeLine, logo, brandStyle) {
    const showLogo = !!logo && brandStyle !== BRAND_STYLES.NAME;
    const showName = !!storeLine && (!showLogo || brandStyle === BRAND_STYLES.BOTH);
    let nameMaxWidth = CONTENT_WIDTH;

    if (showLogo) {
        const scale = Math.min(BRAND_ROW.height / logo.height, BRAND.logoMaxWidth / logo.width);
        const width = logo.width * scale;
        const height = logo.height * scale;
        page.drawImage(logo, { x: CONTENT_LEFT, y: BRAND.bottom + (BRAND_ROW.height - height) / 2, width, height });
        nameMaxWidth = CONTENT_WIDTH - width - 16;
    }
    if (!showName) return;

    const name = fitText(storeLine, fonts.bold, BRAND.nameSize, nameMaxWidth, 8);
    const capHeight = fonts.bold.heightAtSize(name.size, { descender: false }) * 0.75;
    const y = BRAND.bottom + (BRAND_ROW.height - capHeight) / 2;
    if (showLogo) {
        drawRight(page, name.text, fonts.bold, name.size, CONTENT_LEFT + CONTENT_WIDTH, y, TEAL);
    } else {
        page.drawText(name.text, { x: CONTENT_LEFT, y, size: name.size, font: fonts.bold, color: TEAL });
    }
}

function drawHeader(page, fonts, header, pageNumber, pageCount) {
    page.drawRectangle({ x: CONTENT_LEFT, y: HEADER.barBottom, width: CONTENT_WIDTH, height: HEADER.barTop - HEADER.barBottom, color: TEAL });
    page.drawRectangle({ x: CONTENT_LEFT, y: HEADER.barBottom - HEADER.ruleHeight, width: CONTENT_WIDTH, height: HEADER.ruleHeight, color: ACCENT_GREEN });

    drawRight(page, `PAGE ${pageNumber} OF ${pageCount}`, fonts.bold, 8, TEXT_RIGHT, HEADER.pageLabelY, WHITE);
    page.drawText(header.title, { x: TEXT_LEFT, y: HEADER.titleY, size: 25, font: fonts.bold, color: TITLE_MINT });
    drawRight(page, header.dateText, fonts.bold, 12, TEXT_RIGHT, HEADER.dateY, WHITE);
}

function drawLegend(page, fonts) {
    page.drawText("LEGEND", { x: TEXT_LEFT, y: LEGEND.titleY, size: 14, font: fonts.bold, color: TEAL });

    const heading = (text, x) => page.drawText(text, { x, y: LEGEND.headerY, size: 10, font: fonts.bold, color: INK });
    const row = (text, x, i, font = fonts.regular) => page.drawText(text, { x, y: LEGEND.rowY[i], size: 10, font, color: INK });

    heading("Common Effects", LEGEND.effectsX);
    MENU_COLORS.forEach((color, i) => {
        const { x, width, height, firstBottom } = LEGEND.swatch;
        const bottom = firstBottom - i * LEGEND.rowPitch;
        page.drawRectangle({ x, y: bottom, width, height, color: COLOR_FILL[color] });
        drawCentered(page, color.toUpperCase(), fonts.bold, 8, x, width, bottom + 4.18, labelInk(color));
        row(EFFECT_BY_COLOR[color], LEGEND.effectTextX, i);
    });

    heading("Potency", LEGEND.potencyX);
    Object.values(POTENCY_TIERS).forEach((tier, i) => {
        const bottom = LEGEND.potencyBar.firstBottom - i * LEGEND.rowPitch;
        drawPotencyBars(page, LEGEND.potencyX, bottom, LEGEND.potencyBar, tier.bars, LEGEND.potencyBar.pitch);
        row(tier.label, LEGEND.potencyTextX, i);
    });

    heading("Aroma", LEGEND.aromaX);
    MENU_COLORS.forEach((color, i) => row(AROMA_BY_COLOR[color], LEGEND.aromaX, i));

    heading("DEMS Ratio", LEGEND.demsX);
    page.drawText("(Desired Experience,", { x: LEGEND.demsX, y: LEGEND.demsNoteY[0], size: 7.2, font: fonts.bold, color: INK });
    page.drawText("Minimum Side-effects)", { x: LEGEND.demsX, y: LEGEND.demsNoteY[1], size: 7.2, font: fonts.bold, color: INK });
    Object.values(DEMS_TIERS).forEach((tier, i) => {
        const code = `${tier.code}: `;
        row(code, LEGEND.demsX, i + 1, fonts.medium);
        row(tier.label, LEGEND.demsX + fonts.medium.widthOfTextAtSize(code, 10), i + 1);
    });

    for (const x of LEGEND.dividers) {
        page.drawLine({ start: { x, y: LEGEND.dividerTop }, end: { x, y: LEGEND.dividerBottom }, thickness: 0.5, color: INK });
    }
}

function drawCard(page, fonts, card) {
    const { x, y } = card;
    const tabRight = x + CARD_PARTS.tabWidth;
    const right = x + CARD.width;
    const line = (start, end) => page.drawLine({ start, end, thickness: CARD_PARTS.stroke, color: INK });

    page.drawRectangle({ x, y, width: CARD_PARTS.tabWidth, height: CARD.height, color: COLOR_FILL[card.color] });
    page.drawRectangle({ x, y, width: CARD.width, height: CARD.height, borderColor: INK, borderWidth: CARD_PARTS.stroke });

    const dividerY = y + CARD_PARTS.lowerRowHeight;
    const barsRight = tabRight + CARD_PARTS.barsCellWidth;
    const aromaRight = barsRight + CARD_PARTS.aromaCellWidth;
    line({ x: tabRight, y }, { x: tabRight, y: y + CARD.height });
    line({ x: tabRight, y: dividerY }, { x: right, y: dividerY });
    line({ x: barsRight, y }, { x: barsRight, y: dividerY });
    line({ x: aromaRight, y }, { x: aromaRight, y: dividerY });

    // Color name runs bottom-to-top, centered in the tab.
    const tabLabel = card.color.toUpperCase();
    const tabSize = 8;
    const capHeight = fonts.bold.heightAtSize(tabSize, { descender: false }) * 0.75;
    page.drawText(tabLabel, {
        x: x + CARD_PARTS.tabWidth / 2 + capHeight / 2,
        y: y + (CARD.height - fonts.bold.widthOfTextAtSize(tabLabel, tabSize)) / 2,
        size: tabSize,
        font: fonts.bold,
        color: labelInk(card.color),
        rotate: degrees(90),
    });

    const nameWidth = right - tabRight - CARD_PARTS.namePadX * 2;
    const name = fitText(card.name, fonts.bold, 10, nameWidth);
    page.drawText(name.text, { x: tabRight + CARD_PARTS.namePadX, y: y + CARD_PARTS.nameBaseline, size: name.size, font: fonts.bold, color: INK });

    const { bar } = CARD_PARTS;
    drawPotencyBars(page, tabRight + bar.firstX, y + bar.bottom, bar, card.potencyBars, bar.pitch);
    drawCentered(page, card.aroma, fonts.medium, CARD_PARTS.cellTextSize, barsRight, CARD_PARTS.aromaCellWidth, y + CARD_PARTS.cellBaseline);
    drawCentered(page, card.demsLabel, fonts.medium, CARD_PARTS.cellTextSize, aromaRight, right - aromaRight, y + CARD_PARTS.cellBaseline);
}

function drawSection(page, fonts, section) {
    const { barBottom } = section;
    page.drawRectangle({ x: CONTENT_LEFT, y: barBottom, width: CONTENT_WIDTH, height: SECTION.barHeight, color: TEAL });
    const title = fitText(section.title, fonts.bold, 14, CONTENT_WIDTH - (TEXT_LEFT - CONTENT_LEFT) * 2, 9);
    page.drawText(title.text, { x: TEXT_LEFT, y: barBottom + 4.704, size: title.size, font: fonts.bold, color: WHITE });

    page.drawText("Price", { x: PRICE.headingX, y: barBottom - PRICE.headingDrop, size: 10, font: fonts.bold, color: INK });
    section.prices.forEach((price, i) => {
        const y = barBottom - PRICE.firstRowDrop - i * PRICE.rowPitch;
        page.drawText(price.label, { x: PRICE.labelX, y, size: 10, font: fonts.bold, color: INK });
        const value = fitText(price.text, fonts.regular, 10, CARD.columns[0] - PRICE.valueX - 4);
        page.drawText(value.text, { x: PRICE.valueX, y, size: value.size, font: fonts.regular, color: INK });
    });

    for (const card of section.cards) drawCard(page, fonts, card);
}

/**
 * Build the menu PDF.
 *
 * @param {object} model - From buildMenuModel(); must have no blockers.
 * @param {{ regular: ArrayBuffer|Uint8Array, medium: ArrayBuffer|Uint8Array, bold: ArrayBuffer|Uint8Array }} fontBytes
 * @param {{ bytes: ArrayBuffer|Uint8Array, type: "png"|"jpg" }|null} [logo] - License logo, used when
 *   model.brandStyle asks for it; the name is drawn instead when it is missing or cannot be read.
 * @returns {Promise<Uint8Array>} PDF file bytes.
 */
export async function buildMenuPdf(model, fontBytes, logo = null) {
    const doc = await PDFDocument.create();
    doc.registerFontkit(fontkit);
    doc.setTitle([model.header.storeLine, model.header.title, model.header.dateText].filter(Boolean).join(" — "));

    const fonts = {
        regular: await doc.embedFont(fontBytes.regular, { subset: true }),
        medium: await doc.embedFont(fontBytes.medium, { subset: true }),
        bold: await doc.embedFont(fontBytes.bold, { subset: true }),
    };

    let logoImage = null;
    if (logo && model.brandStyle !== BRAND_STYLES.NAME) {
        try {
            logoImage = logo.type === "jpg" ? await doc.embedJpg(logo.bytes) : await doc.embedPng(logo.bytes);
        } catch {
            // Unreadable image — fall back to the name-only layout.
        }
    }

    const pages = paginateMenu(model.sections);
    pages.forEach((layoutPage, index) => {
        const page = doc.addPage([PAGE.width, PAGE.height]);
        drawBrandRow(page, fonts, model.header.storeLine, logoImage, model.brandStyle);
        drawHeader(page, fonts, model.header, index + 1, pages.length);
        drawLegend(page, fonts);
        for (const section of layoutPage.sections) drawSection(page, fonts, section);
    });

    return doc.save();
}

async function loadFont(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to load menu font: ${url}`);
    return res.arrayBuffer();
}

// Re-encode an image the PDF cannot embed directly (WebP, SVG, GIF) as PNG.
async function toPngBytes(blob) {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    const png = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    return png.arrayBuffer();
}

/**
 * Fetch the license logo in a form the PDF can embed.
 * The image comes through our own API: the stored logo is a public S3 URL, and
 * the browser will not let page code read another site's file directly.
 *
 * @param {string} [licenseNo]
 * @returns {Promise<{ bytes: ArrayBuffer, type: "png"|"jpg" }|null>} null when the license has no
 *   logo or it cannot be loaded.
 */
export async function loadMenuLogo(licenseNo) {
    if (!licenseNo) return null;
    try {
        const res = await apiClient.get(`/license/logo?licenseNo=${encodeURIComponent(licenseNo)}`, { responseType: "blob" });
        const blob = res.data;
        if (blob.type === "image/png") return { bytes: await blob.arrayBuffer(), type: "png" };
        if (blob.type === "image/jpeg") return { bytes: await blob.arrayBuffer(), type: "jpg" };
        return { bytes: await toPngBytes(blob), type: "png" };
    } catch {
        return null;
    }
}

/**
 * Build the menu PDF and download it in the browser.
 *
 * @param {object} model    - From buildMenuModel(); must have no blockers.
 * @param {string} filename - Download name, including ".pdf".
 * @param {{ licenseNo?: string }} [options] - License whose logo to print, when the brand style uses one.
 */
export async function generateMenuPdf(model, filename, { licenseNo } = {}) {
    const [regular, medium, bold, logo] = await Promise.all([
        ...[interRegularUrl, interMediumUrl, interBoldUrl].map(loadFont),
        model.brandStyle === BRAND_STYLES.NAME ? null : loadMenuLogo(licenseNo),
    ]);
    const bytes = await buildMenuPdf(model, { regular, medium, bold }, logo);

    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
